import { Alert, Box, Button, CircularProgress, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyboardEvent, type ReactNode, useEffect, useState } from 'react';

// Types
import { ApiError } from '../../@types';
import { DeactivateOtherOutcomesQueryMode, SuperAdminFlags, SuperAdminProviderStatuses } from '../../@types/api';

// Constants
import { QUERY_KEYS } from '../../constants';
import API_ROUTES from '../../constants/apiRoutes';
import {
  BOOLEAN_SETTINGS,
  NUMERIC_SETTINGS,
  REDIS_FLAG_KEYS,
  FLAG_GROUPS,
  QUERY_MODE_OPTIONS,
  PROVIDER_STATUS_SETTINGS,
  type BooleanField,
  type NumericField,
} from '../../constants/superAdminFlags';

// Hooks
import { useModal } from '../../contexts/ModalContext';
import { useToastErrorMessage } from '../../hooks/useToastErrorMessage';

// Utils
import { getData, patchData } from '../../utils/api';
import { mergeSx } from '../../utils/styles';

// Components
import NumberInput from '../atoms/NumberInput';
import Switch from '../atoms/Switch';
import ConfirmationModal from '../modals/ConfirmationModal';

const styles = {
  page: {
    width: '100%',
    maxWidth: 1440,
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 3fr) minmax(0, 2fr)' },
    gap: 3,
  },
  leftColumn: {
    minWidth: 0,
  },
  rightColumn: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: { xs: 'stretch', xl: 'flex-end' },
  },
  section: {
    p: { xs: 2, md: 3 },
    borderRadius: 2,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 280px' },
    py: 2,
    gap: { xs: 2, md: 3 },
    alignItems: 'center',
    '&:not(:last-of-type)': {
      borderBottom: '1px solid',
      borderColor: 'divider',
    },
  },
  rowText: {
    minWidth: 0,
    pr: { md: 2 },
  },
  fieldTitle: {
    fontWeight: 600,
  },
  control: {
    width: '100%',
    minWidth: 0,
    alignItems: { xs: 'flex-start', md: 'stretch' },
  },
  controlRow: {
    width: '100%',
    justifyContent: { xs: 'space-between', md: 'flex-end' },
  },
  statusSlot: {
    width: 18,
    height: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  helperText: {
    mt: 0.5,
  },
  providerCard: {
    width: { xs: '100%', xl: 'fit-content' },
    maxWidth: '100%',
    minWidth: { xs: '100%', xl: 320 },
    p: { xs: 2, md: 3 },
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
  },
  providerCardTitle: {
    fontWeight: 700,
  },
  providerStatusGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
    gap: 1.5,
  },
  providerStatusItem: {
    p: 1.5,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
  },
  providerStatusItemHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1,
  },
  providerStatusDot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    flexShrink: 0,
    border: '2px solid',
    borderColor: 'common.white',
    boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.14)',
  },
  spinnerWrapper: {
    minHeight: '40vh',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

const getDirtyHelperText = (isDirty: boolean, isSaving: boolean) => {
  if (isSaving) {
    return 'Saving...';
  }

  if (isDirty) {
    return 'Press Enter to save';
  }

  return ' ';
};

const getNumericDraft = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? String(value) : '');

type Props = {
  title: string;
  description: string;
  settings: typeof BOOLEAN_SETTINGS;
  disabledReason?: string;
  children?: ReactNode;
};

const SuperAdminFlagControls = ({ title, description, settings: booleanSettings, disabledReason, children }: Props) => {
  const [search, setSearch] = useState('');
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();
  const { handleErrorMessage } = useToastErrorMessage();

  const { data, isPending, isError, error } = useQuery<SuperAdminFlags, ApiError>({
    queryKey: [QUERY_KEYS.superAdminFlags],
    queryFn: () => getData(API_ROUTES.SUPER_ADMIN_FLAGS, undefined, 'ftbConsumer'),
    refetchOnWindowFocus: true,
  });
  const {
    data: providerStatuses,
    isPending: isProviderStatusesLoading,
    isError: isProviderStatusesError,
    error: providerStatusesError,
  } = useQuery<SuperAdminProviderStatuses, ApiError>({
    queryKey: [QUERY_KEYS.superAdminProviderStatuses],
    queryFn: () => getData(API_ROUTES.SUPER_ADMIN_PROVIDER_STATUSES, undefined, 'ftbConsumer'),
  });

  const [draftFlags, setDraftFlags] = useState<SuperAdminFlags | null>(null);
  const [numericDrafts, setNumericDrafts] = useState<Record<NumericField, string>>({
    ftbResolveBufferMaxDelayedJobs: '',
    ftbResolveBufferDelayS: '',
    resolveBettingSlipsBatchSize: '',
  });
  const [pendingFields, setPendingFields] = useState<Partial<Record<keyof SuperAdminFlags, boolean>>>({});

  useEffect(() => {
    if (data) {
      setDraftFlags(data);
      setNumericDrafts({
        ftbResolveBufferMaxDelayedJobs: getNumericDraft(data.ftbResolveBufferMaxDelayedJobs),
        ftbResolveBufferDelayS: getNumericDraft(data.ftbResolveBufferDelayS),
        resolveBettingSlipsBatchSize: getNumericDraft(data.resolveBettingSlipsBatchSize),
      });
    }
  }, [data]);

  useEffect(() => {
    if (isError && error) {
      handleErrorMessage(error);
    }
  }, [error, handleErrorMessage, isError]);

  useEffect(() => {
    if (isProviderStatusesError && providerStatusesError) {
      handleErrorMessage(providerStatusesError);
    }
  }, [handleErrorMessage, isProviderStatusesError, providerStatusesError]);

  const updateMutation = useMutation<void, ApiError, Partial<SuperAdminFlags>>({
    mutationFn: async (payload) => {
      await patchData(API_ROUTES.SUPER_ADMIN_FLAGS, payload, 'ftbConsumer');
    },
  });

  const setFieldPending = (field: keyof SuperAdminFlags, isPending: boolean) => {
    setPendingFields((current) => ({
      ...current,
      [field]: isPending,
    }));
  };

  const refetchFlags = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.superAdminFlags] }),
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.superAdminProviderStatuses] }),
    ]);
  };

  const isSavingAnyField = Object.values(pendingFields).some(Boolean);
  const controlsDisabled = isSavingAnyField || isError || Boolean(disabledReason);
  const normalizedSearch = search.trim().toLowerCase();
  const matchesSearch = (setting: { label: string; description: string; field?: keyof SuperAdminFlags }) =>
    `${setting.label} ${setting.description} ${setting.field ? REDIS_FLAG_KEYS[setting.field] : ''}`
      .toLowerCase()
      .includes(normalizedSearch);
  const showQueryMode = matchesSearch({
    label: 'Outcome Deactivation Query Mode',
    description: 'FTB consumer',
    field: 'deactivateOtherOutcomesQueryMode',
  });

  const saveQueryMode = (value: DeactivateOtherOutcomesQueryMode) => {
    setFieldPending('deactivateOtherOutcomesQueryMode', true);
    updateMutation.mutate(
      { deactivateOtherOutcomesQueryMode: value },
      {
        onSuccess: refetchFlags,
        onError: handleErrorMessage,
        onSettled: () => setFieldPending('deactivateOtherOutcomesQueryMode', false),
      }
    );
  };

  const revertBooleanField = (field: BooleanField) => {
    if (!data) {
      return;
    }

    setDraftFlags((current) =>
      current
        ? {
            ...current,
            [field]: data[field],
          }
        : current
    );
  };

  const revertNumericField = (field: NumericField) => {
    if (!data) {
      return;
    }

    setNumericDrafts((current) => ({
      ...current,
      [field]: getNumericDraft(data[field]),
    }));
  };

  const saveBooleanField = (field: BooleanField, value: boolean) => {
    setDraftFlags((current) =>
      current
        ? {
            ...current,
            [field]: value,
          }
        : current
    );

    setFieldPending(field, true);

    updateMutation.mutate(
      { [field]: value },
      {
        onSuccess: async () => {
          await refetchFlags();
        },
        onError: (mutationError) => {
          handleErrorMessage(mutationError);
          revertBooleanField(field);
        },
        onSettled: () => {
          setFieldPending(field, false);
        },
      }
    );
  };

  const openBooleanConfirmation = (field: BooleanField, label: string, nextValue: boolean) => {
    openModal(
      <ConfirmationModal
        title={`Are you sure you want to ${nextValue ? 'enable' : 'disable'} ${label}?`}
        actionButtonLabel='Confirm'
        handleConfirm={() => saveBooleanField(field, nextValue)}
        closeModal={closeModal}
        actionButtonClose='Cancel'
      />
    );
  };

  const openQueryModeConfirmation = (value: DeactivateOtherOutcomesQueryMode) => {
    openModal(
      <ConfirmationModal
        title={`Change Outcome Deactivation Query Mode to ${QUERY_MODE_OPTIONS.find((option) => option.value === value)?.label}?`}
        actionButtonLabel='Confirm'
        handleConfirm={() => saveQueryMode(value)}
        closeModal={closeModal}
        actionButtonClose='Cancel'
      />
    );
  };

  const saveNumericField = (field: NumericField) => {
    const draftValue = numericDrafts[field];
    const parsedValue = Number(draftValue);

    if (!data || !Number.isFinite(data[field])) {
      revertNumericField(field);
      return;
    }

    if (
      !draftValue ||
      !Number.isFinite(parsedValue) ||
      (field === 'resolveBettingSlipsBatchSize' && (!Number.isInteger(parsedValue) || parsedValue < 1))
    ) {
      revertNumericField(field);
      return;
    }

    if (parsedValue === data[field]) {
      return;
    }

    setFieldPending(field, true);

    updateMutation.mutate(
      { [field]: parsedValue },
      {
        onSuccess: async () => {
          await refetchFlags();
        },
        onError: (mutationError) => {
          handleErrorMessage(mutationError);
          revertNumericField(field);
        },
        onSettled: () => {
          setFieldPending(field, false);
        },
      }
    );
  };

  const handleNumericChange = (field: NumericField) => (value: string | number | null) => {
    setNumericDrafts((current) => ({
      ...current,
      [field]: value == null ? '' : String(value),
    }));
  };

  const handleNumericKeyDown = (field: NumericField) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveNumericField(field);
    }
  };

  const renderNumericSettings = (settings: typeof NUMERIC_SETTINGS) => (
    <>
      {settings.filter(matchesSearch).map(({ field, label, description, group }) => {
        const serverValue = data?.[field];
        const isSupported = Number.isFinite(serverValue);
        const isDirty = isSupported && numericDrafts[field] !== getNumericDraft(serverValue);
        const isSaving = Boolean(pendingFields[field]);
        const helperText = getDirtyHelperText(isDirty, isSaving).trim();

        return (
          <Stack key={field} sx={styles.row}>
            <Stack sx={styles.rowText}>
              <Typography variant='h6' sx={styles.fieldTitle}>
                {label}
              </Typography>
              {group === 'Redis controls' && (
                <Typography variant='caption' color='text.secondary' sx={{ overflowWrap: 'anywhere' }}>
                  Redis key: {REDIS_FLAG_KEYS[field]}
                </Typography>
              )}
              <Typography
                variant='body2'
                sx={{
                  color: 'text.secondary',
                }}
              >
                {description}
              </Typography>
            </Stack>

            <Stack spacing={helperText ? 0.5 : 0} sx={styles.control}>
              <Stack
                direction='row'
                sx={{
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                }}
              >
                <Box sx={styles.statusSlot}>{isSaving ? <CircularProgress size={18} /> : null}</Box>
                <NumberInput
                  label={group === 'Redis controls' ? label : undefined}
                  value={isSupported ? numericDrafts[field] : ''}
                  onChange={handleNumericChange(field)}
                  onKeyDown={handleNumericKeyDown(field)}
                  disabled={controlsDisabled || !isSupported}
                  size='small'
                  fullWidth
                  valueAsString
                />
              </Stack>
              {helperText ? (
                <Typography
                  variant='caption'
                  sx={mergeSx(
                    {
                      color: 'text.secondary',
                    },
                    styles.helperText
                  )}
                >
                  {helperText}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        );
      })}
    </>
  );

  const isFlagsLoading = !isError && (isPending || !draftFlags);

  if (isFlagsLoading) {
    return (
      <Stack spacing={3} sx={styles.page}>
        {children}
        <Stack sx={styles.spinnerWrapper}>
          <CircularProgress />
        </Stack>
      </Stack>
    );
  }

  if (!draftFlags) {
    return (
      <Stack spacing={1}>
        <Typography variant='h4'>{title}</Typography>
        {children}
        <Typography color='error'>Failed to load super admin flags.</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={styles.page}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ justifyContent: 'space-between' }}>
        <Stack spacing={1}>
          <Typography variant='h4'>{title}</Typography>
          <Typography color='text.secondary'>{description}</Typography>
        </Stack>
        <Button onClick={() => void refetchFlags()} disabled={isSavingAnyField}>
          Refresh flags
        </Button>
      </Stack>
      {children}
      {disabledReason && <Alert severity='warning'>{disabledReason}</Alert>}
      <TextField
        label='Search feature flags'
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        size='small'
        fullWidth
      />
      {isError && <Alert severity='error'>Could not refresh flags. Refresh before making further changes.</Alert>}
      <Box sx={styles.contentGrid}>
        <Stack spacing={3} sx={styles.leftColumn}>
          {FLAG_GROUPS.map((group) => {
            const settings = booleanSettings.filter((setting) => setting.group === group && matchesSearch(setting));
            const numericSettings = NUMERIC_SETTINGS.filter(
              (setting) => setting.group === 'Redis controls' && group === 'Redis controls'
            );
            if (!settings.length && !numericSettings.some(matchesSearch)) return null;
            return (
              <Paper
                key={group}
                elevation={0}
                sx={styles.section}
                role={group === 'Redis controls' ? 'region' : undefined}
                aria-label={group === 'Redis controls' ? group : undefined}
              >
                <Typography variant='h5'>{group}</Typography>
                <Stack spacing={0}>
                  {settings.map(({ field, label, description }) => (
                    <Stack key={field} sx={styles.row}>
                      <Stack sx={styles.rowText}>
                        <Typography variant='h6' sx={styles.fieldTitle}>
                          {label}
                        </Typography>
                        {group === 'Redis controls' && (
                          <Typography variant='caption' color='text.secondary' sx={{ overflowWrap: 'anywhere' }}>
                            Redis key: {REDIS_FLAG_KEYS[field]}
                          </Typography>
                        )}
                        <Typography
                          variant='body2'
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {description}
                        </Typography>
                      </Stack>

                      <Stack
                        direction='row'
                        sx={mergeSx(
                          {
                            alignItems: 'center',
                            gap: 1,
                          },
                          mergeSx(styles.control, styles.controlRow)
                        )}
                      >
                        <Box sx={styles.statusSlot}>{pendingFields[field] ? <CircularProgress size={18} /> : null}</Box>
                        <Switch
                          value={draftFlags[field]}
                          onChange={(_event, checked) => openBooleanConfirmation(field, label, checked)}
                          slotProps={{ input: { 'aria-label': label } }}
                          disabled={controlsDisabled || typeof draftFlags[field] !== 'boolean'}
                        />
                      </Stack>
                    </Stack>
                  ))}
                  {renderNumericSettings(numericSettings)}
                </Stack>
              </Paper>
            );
          })}

          {showQueryMode && (
            <Paper elevation={0} sx={styles.section}>
              <Stack spacing={2}>
                <Typography variant='h6'>Outcome Deactivation Query Mode</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Choose how FTB deactivates missing outcomes. Used when Deactivate Other Outcomes is enabled.
                </Typography>
                <TextField
                  select
                  label='Outcome Deactivation Query Mode'
                  value={draftFlags.deactivateOtherOutcomesQueryMode ?? ''}
                  onChange={(event) =>
                    openQueryModeConfirmation(event.target.value as DeactivateOtherOutcomesQueryMode)
                  }
                  disabled={controlsDisabled || !draftFlags.deactivateOtherOutcomesQueryMode}
                  fullWidth
                  size='small'
                >
                  {QUERY_MODE_OPTIONS.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Paper>
          )}

          {NUMERIC_SETTINGS.some((setting) => setting.group !== 'Redis controls' && matchesSearch(setting)) && (
            <Paper elevation={0} sx={styles.section}>
              <Stack spacing={0}>
                {renderNumericSettings(NUMERIC_SETTINGS.filter(({ group }) => group !== 'Redis controls'))}
              </Stack>
            </Paper>
          )}
          {!booleanSettings.some(matchesSearch) && !NUMERIC_SETTINGS.some(matchesSearch) && !showQueryMode && (
            <Typography color='text.secondary'>No feature flags match your search.</Typography>
          )}
        </Stack>

        <Box sx={styles.rightColumn}>
          <Paper elevation={0} sx={styles.providerCard}>
            <Stack spacing={2}>
              <Typography variant='h6' sx={styles.providerCardTitle}>
                Provider Running Flags
              </Typography>

              <Box sx={styles.providerStatusGrid}>
                {PROVIDER_STATUS_SETTINGS.map(({ field, label }) => {
                  const isLoadingStatus = isProviderStatusesLoading && !providerStatuses;
                  const statusValue = providerStatuses?.[field];
                  const statusLabel = providerStatuses == null ? 'Unavailable' : statusValue ? 'Active' : 'Inactive';

                  return (
                    <Stack key={field} sx={styles.providerStatusItem}>
                      <Stack direction='row' sx={styles.providerStatusItemHeader}>
                        <Typography variant='subtitle1' sx={styles.fieldTitle}>
                          {label}
                        </Typography>

                        {isLoadingStatus ? (
                          <CircularProgress size={18} />
                        ) : (
                          <Box
                            role='status'
                            aria-label={`${label} ${statusLabel}`}
                            sx={mergeSx(styles.providerStatusDot, {
                              backgroundColor:
                                providerStatuses == null
                                  ? 'text.disabled'
                                  : statusValue
                                    ? 'success.main'
                                    : 'error.main',
                            })}
                          />
                        )}
                      </Stack>
                    </Stack>
                  );
                })}
              </Box>
            </Stack>
          </Paper>
        </Box>
      </Box>
    </Stack>
  );
};

export default SuperAdminFlagControls;
